import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Plus, Key, RotateCw, Power, Copy, CheckCircle2, AlertTriangle, Smartphone, Clock,
  ShieldCheck, ShieldOff,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SCOPE_OPTIONS = [
  { value: 'read:public',           label: 'Full public catalog (branches, products, rates, app-config)', recommended: true },
  { value: 'read:branches',         label: 'Branch directory only' },
  { value: 'read:products',         label: 'Loan / savings / share / lease product catalogs' },
  { value: 'read:rates',            label: 'SARB repo + prime rates' },
  { value: 'read:cross-border-countries', label: 'Supported destination countries for cross-border payments' },
  { value: 'read:app-config',       label: 'App config — min version, feature flags' },
];

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-ZA', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const CopyButton = ({ text, label = 'Copy' }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      data-testid="copy-api-key-btn"
      onClick={async () => {
        try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }
        catch { toast.error('Clipboard unavailable — select and copy manually'); }
      }}
    >
      {copied ? <CheckCircle2 className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />}
      {copied ? 'Copied' : label}
    </Button>
  );
};

// ============================================================================
// Dialog shown exactly once after a key is issued — lets the admin copy it.
// After it's dismissed, the plaintext key is never shown again.
// ============================================================================
const ApiKeyRevealDialog = ({ open, onClose, apiKey, accountName, mode = 'created' }) => {
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby="api-key-reveal-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#C5A059]" />
            API Key {mode === 'created' ? 'Created' : 'Rotated'}
          </DialogTitle>
          <DialogDescription id="api-key-reveal-desc">
            {mode === 'created' ? `Service account "${accountName}" is ready.` : `A new key has been issued for "${accountName}".`}
            {' '}Save this key now — you will <b>not</b> be able to view it again.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            Treat this key like a password. Store it in the mobile app's secure config
            (Android Keystore / iOS Keychain / AWS Secrets Manager). {mode === 'rotated' && (
              <b className="block mt-1">The previous key has been revoked — update your mobile app before it starts failing.</b>
            )}
          </div>
        </div>

        <div className="mt-3">
          <Label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">API Key</Label>
          <div className="flex gap-2">
            <Input
              readOnly
              value={apiKey}
              className="font-mono text-xs"
              onFocus={e => e.target.select()}
              data-testid="api-key-value"
            />
            <CopyButton text={apiKey} />
          </div>
          <p className="text-[10px] text-gray-500 mt-1">
            Use this in <code>POST /api/service-auth/token</code> to obtain a 90-day JWT.
          </p>
        </div>

        <DialogFooter>
          <Button onClick={onClose} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="reveal-close-btn">
            I&apos;ve saved the key
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Create-account form
// ============================================================================
const CreateAccountDialog = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(['read:public']);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setName(''); setScopes(['read:public']); } }, [open]);

  const toggleScope = (s) => {
    setScopes(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Give this account a name'); return; }
    if (scopes.length === 0) { toast.error('Pick at least one scope'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/service-auth/accounts', {
        name: name.trim(),
        role: 'mobile_api',
        scopes,
      });
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create service account');
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby="new-svc-desc">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">New Service Account</DialogTitle>
          <DialogDescription id="new-svc-desc">
            Issues a read-only API key for a machine-to-machine caller (e.g. the mobile app backend).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="new-service-account-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Name *</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Mobile App — Production"
              data-testid="svc-name-input"
              required
            />
            <p className="text-[10px] text-gray-400 mt-1">Shown in audit logs so you can tell which caller is which.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500">Role</Label>
            <div className="mt-1 px-3 py-2 border border-gray-200 bg-gray-50 text-sm flex items-center gap-2">
              <Smartphone className="w-4 h-4 text-[#C5A059]" />
              <span>mobile_api</span>
              <span className="text-[10px] text-gray-400">(read-only — no write/admin access)</span>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">Scopes *</Label>
            <div className="space-y-1.5 border border-gray-200 p-2">
              {SCOPE_OPTIONS.map(opt => (
                <label key={opt.value}
                       className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 p-1.5 rounded"
                       data-testid={`scope-${opt.value}`}>
                  <input
                    type="checkbox"
                    checked={scopes.includes(opt.value)}
                    onChange={() => toggleScope(opt.value)}
                    className="mt-0.5 accent-[#C5A059]"
                  />
                  <div className="flex-1">
                    <div className="text-sm flex items-center gap-2">
                      <code className="text-[11px] bg-gray-100 px-1">{opt.value}</code>
                      {opt.recommended && (
                        <span className="text-[9px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 border border-emerald-200">
                          RECOMMENDED
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500">{opt.label}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting}
                    className="bg-[#1f1f1f] hover:bg-[#333333] text-white"
                    data-testid="create-svc-submit-btn">
              <Key className="w-4 h-4 mr-2" />
              {submitting ? 'Creating…' : 'Create & Issue Key'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// Main page
// ============================================================================
const ServiceAccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [reveal, setReveal] = useState(null);       // { apiKey, name, mode }
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/service-auth/accounts');
      setAccounts(data.service_accounts || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load service accounts');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreated = (resp) => {
    setShowCreate(false);
    setReveal({ apiKey: resp.api_key, name: resp.name, mode: 'created' });
    load();
  };

  const handleRotate = async (acc) => {
    if (!window.confirm(`Rotate the API key for "${acc.name}"?\n\nThe current key will stop working IMMEDIATELY. Make sure your mobile app can pick up the new key before rotating.`)) return;
    setBusyId(acc.id);
    try {
      const { data } = await api.post(`/service-auth/accounts/${acc.id}/rotate-key`);
      setReveal({ apiKey: data.api_key, name: acc.name, mode: 'rotated' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Rotate failed'); }
    setBusyId(null);
  };

  const handleToggle = async (acc) => {
    const enable = !acc.is_active;
    const action = enable ? 'enable' : 'disable';
    if (!window.confirm(`${enable ? 'Enable' : 'Disable'} "${acc.name}"?\n\n${enable ? 'The mobile app will be able to obtain tokens again.' : 'All existing JWTs will stop working immediately.'}`)) return;
    setBusyId(acc.id);
    try {
      await api.post(`/service-auth/accounts/${acc.id}/${action}`);
      toast.success(enable ? 'Service account enabled' : 'Service account disabled');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || `${action} failed`); }
    setBusyId(null);
  };

  return (
    <AdminSubPage
      title="Service Accounts"
      breadcrumb={['Admin', 'System', 'Service Accounts']}
      backPath="/dashboard/admin/system"
      actionLabel="New Service Account"
      onAction={() => setShowCreate(true)}
    >
      <div className="bg-[#FFFDF5] border border-[#C5A059]/40 p-4 mb-6 text-xs text-[#8B7339] flex gap-3">
        <ShieldCheck className="w-5 h-5 shrink-0 text-[#C5A059] mt-0.5" />
        <div>
          <p className="font-semibold text-[#1f1f1f] mb-1">What are service accounts?</p>
          <p>
            Non-interactive credentials your mobile app uses to fetch <b>system-wide read-only data</b>
            (branches, product catalog, SARB rates, feature flags) without using staff or admin credentials.
            Each account gets an API key it exchanges at <code className="bg-white px-1 border border-[#C5A059]/40">POST /api/service-auth/token</code>
            {' '}for a 90-day JWT. Revoke instantly by clicking <b>Disable</b>.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-400 py-12">Loading service accounts…</p>
      ) : accounts.length === 0 ? (
        <div className="text-center border border-dashed border-gray-300 p-12" data-testid="no-svc-accounts">
          <Key className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-600">No service accounts yet</p>
          <p className="text-xs text-gray-400 mt-1">Create one so your mobile app can stop using admin credentials.</p>
          <Button onClick={() => setShowCreate(true)}
                  className="mt-4 bg-[#1f1f1f] hover:bg-[#333333] text-white"
                  data-testid="empty-state-new-btn">
            <Plus className="w-4 h-4 mr-2" />Create your first service account
          </Button>
        </div>
      ) : (
        <div className="border border-gray-200 bg-white overflow-hidden" data-testid="svc-accounts-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2.5">Name</th>
                <th className="px-4 py-2.5">Key prefix</th>
                <th className="px-4 py-2.5">Scopes</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Last used</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(acc => (
                <tr key={acc.id} className="border-b border-gray-100 hover:bg-gray-50/50" data-testid={`svc-row-${acc.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1f1f1f]">{acc.name}</div>
                    <div className="text-[10px] text-gray-400">Created {formatDateTime(acc.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5">{acc.api_key_prefix}…</code>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(acc.scopes || []).map(s => (
                        <span key={s} className="text-[10px] bg-[#FFFDF5] border border-[#C5A059]/40 text-[#8B7339] px-1.5 py-0.5">{s}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {acc.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5">
                        <CheckCircle2 className="w-3 h-3" />ACTIVE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5">
                        <ShieldOff className="w-3 h-3" />DISABLED
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />{formatDateTime(acc.last_used_at)}
                    </div>
                    {acc.last_used_ip && <div className="text-[10px] text-gray-400 font-mono">{acc.last_used_ip}</div>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button
                      size="sm" variant="outline"
                      disabled={busyId === acc.id || !acc.is_active}
                      onClick={() => handleRotate(acc)}
                      className="mr-2"
                      data-testid={`rotate-btn-${acc.id}`}
                    >
                      <RotateCw className="w-3.5 h-3.5 mr-1" />Rotate
                    </Button>
                    <Button
                      size="sm"
                      variant={acc.is_active ? 'outline' : 'default'}
                      disabled={busyId === acc.id}
                      onClick={() => handleToggle(acc)}
                      className={acc.is_active ? 'text-red-700 border-red-200 hover:bg-red-50' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                      data-testid={`toggle-btn-${acc.id}`}
                    >
                      <Power className="w-3.5 h-3.5 mr-1" />{acc.is_active ? 'Disable' : 'Enable'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateAccountDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      <ApiKeyRevealDialog
        open={!!reveal}
        onClose={() => setReveal(null)}
        apiKey={reveal?.apiKey || ''}
        accountName={reveal?.name || ''}
        mode={reveal?.mode || 'created'}
      />
    </AdminSubPage>
  );
};

export default ServiceAccountsPage;
